<script setup lang="ts">
import { onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import type { YMap } from '@yandex/ymaps3-types'
import type { MapEventUpdateHandler } from '@yandex/ymaps3-types/imperative/YMapListener'
import {
  YandexMap,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapListener,
  YandexMapMarker,
  YandexMapResizer,
} from 'vue-yandex-maps'

import AppIcon from '@/components/ui/AppIcon.vue'
import { DEFAULT_CENTER, type LngLat } from '@/composables/useLocation'
import type { MapMarker } from '@/composables/useRestaurants'

/**
 * Карта Яндекса. Пин выбранной точки не двигается — он приклеен к центру
 * экрана, а координаты берутся из центра карты после сдвига: так же, как
 * в веб-версии и в макете.
 */
interface Props {
  center?: LngLat
  zoom?: number
  /** Пин выбранной точки по центру — на экранах выбора адреса */
  marker?: boolean
  /** Пины филиалов */
  markers?: MapMarker[]
  activeMarkerId?: string
}

const props = withDefaults(defineProps<Props>(), {
  center: () => DEFAULT_CENTER,
  zoom: 15,
  marker: true,
  markers: () => [],
  activeMarkerId: '',
})

const emit = defineEmits<{
  moved: [coords: LngLat]
  markerClick: [marker: MapMarker]
}>()

const root = useTemplateRef<HTMLElement>('root')

const map = shallowRef<YMap | null>(null)

/**
 * Стартовые настройки карты. Центр здесь заморожен: если оставить его
 * реактивным, при выборе подсказки карта переедет дважды — один раз по
 * `settings`, второй по `setLocation` ниже, и уедет вдвое дальше нужного.
 */
const settings = { location: { center: props.center, zoom: props.zoom } }

/** Сдвиг карты сыплется десятками событий — отдаём наружу последнее. */
const MOVE_DEBOUNCE = 150
let moveTimer: ReturnType<typeof setTimeout> | undefined

const onUpdate: MapEventUpdateHandler = (event) => {
  if (!props.marker) return
  clearTimeout(moveTimer)
  const [longitude = 0, latitude = 0] = event.location.center
  moveTimer = setTimeout(() => emit('moved', [longitude, latitude]), MOVE_DEBOUNCE)
}

/**
 * Центр пришёл извне (геолокация, выбор подсказки, тап по филиалу) — переезжаем
 * туда. Зум берём текущий, а не из props: клиент мог приблизить карту под себя,
 * и возврат к стартовому зуму выглядит как сброс его настройки.
 */
watch(
  () => props.center,
  (value) => {
    const instance = map.value
    if (instance) instance.setLocation({ center: value, zoom: instance.zoom, duration: 300 })
  },
)

/**
 * Карта берёт размеры контейнера один раз и рисует тайлы со сдвигом, если
 * вёрстка вокруг встала позже. Пересчёт запускает только реальное изменение
 * размера (его ловит YandexMapResizer), поэтому дёргаем контейнер на пиксель.
 * Несколько раз: момент, когда resizer подгрузился с CDN, мы не наблюдаем.
 */
const NUDGE_DELAYS = [200, 800, 2000, 4000]

/** Каждый вызов — реальное изменение высоты на пиксель, чётное число вернёт как было. */
function nudge(): void {
  const el = root.value?.firstElementChild
  if (el instanceof HTMLElement) el.style.bottom = el.style.bottom ? '' : '1px'
}

function nudgeLater(): void {
  for (const delay of NUDGE_DELAYS) setTimeout(nudge, delay)
}

/**
 * Карта создаётся асинхронно, и `settings` замораживают центр на момент
 * монтирования. Если координаты приехали позже (адрес из справочника, ответ
 * геокодера), watch выше срабатывал вхолостую — инстанса ещё не было, и карта
 * оставалась на стартовой точке. Досылаем центр, как только она появилась.
 */
watch(map, (value) => {
  if (!value) return

  const [long, lat] = props.center
  const [startLong, startLat] = settings.location.center
  if (long !== startLong || lat !== startLat) {
    value.setLocation({ center: props.center, zoom: props.zoom })
  }
  nudgeLater()
})

/** Вкладку свернули на время загрузки — карта проснётся с размерами «до». */
function onVisible(): void {
  if (document.visibilityState === 'visible') nudgeLater()
}

onMounted(() => document.addEventListener('visibilitychange', onVisible))
onBeforeUnmount(() => document.removeEventListener('visibilitychange', onVisible))

</script>

<template>
  <div ref="root" class="map-canvas">
    <YandexMap
      v-model="map"
      :settings="settings"
      width="100%"
      height="100%"
      class="map-canvas__map"
    >
      <YandexMapDefaultSchemeLayer />
      <YandexMapDefaultFeaturesLayer />
      <!-- Внутри шита карта монтируется до конца анимации и меряет не ту ширину -->
      <YandexMapResizer />

      <YandexMapMarker
        v-for="item in props.markers"
        :key="item.id"
        :settings="{ coordinates: item.coordinates, onClick: () => emit('markerClick', item) }"
        position="top-center left-center"
      >
        <span
          class="map-canvas__pin"
          :class="{ 'map-canvas__pin--active': item.id === props.activeMarkerId }"
        >
          <AppIcon name="coin-target" :size="item.id === props.activeMarkerId ? 48 : 40" />
        </span>
      </YandexMapMarker>

      <YandexMapListener :settings="{ onUpdate }" />
    </YandexMap>

    <span v-if="props.marker" class="map-canvas__marker">
      <AppIcon name="direct-up" :size="24" />
    </span>

    <slot />
  </div>
</template>

<style scoped lang="scss">
.map-canvas {
  position: relative;
  overflow: hidden;
  background: var(--surface-muted);

  &__map {
    position: absolute;
    inset: 0;
  }

  /*
   * Пин филиала — логотип `coin-target` из макета целиком: тёмный диск с
   * жёлтой мишенью уже внутри иконки, поэтому фон обёртке не нужен. Своей
   * подложкой мы бы сжали мишень до четверти пина, а в макете она занимает
   * три четверти. Выбранный просто крупнее: отдельного состояния в выгрузке
   * нет, а отличать его чем-то надо.
   */
  &__pin {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    filter: drop-shadow(0 2px 6px rgb(0 0 0 / 25%));
  }

  /*
   * Пин выбранной точки приклеен к центру экрана, а не к карте.
   * В выгрузке это узел ровно по центру кадра (x=195 при ширине 390): круг
   * 40×40 `#0f0f05` с иконкой `vuesax/bold/direct-up` внутри. Цвет иконки
   * в экспорте `#292d32` — тёмно-серый на почти чёрном, то есть потерянный
   * при экспорте override; берём светлый, иначе иконки не видно.
   */
  &__marker {
    position: absolute;
    left: 50%;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin: -20px 0 0 -20px;
    border-radius: var(--radius-round);
    background: var(--secondary-950);
    background-color: white;
    color: var(--text-color-inverse);
    pointer-events: none;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  }
}
</style>
